// Fictional annual records, not quotes or financial statements of real businesses.
// Financial amounts are NTD millions, shares are millions, and price is NTD/share.
// Ordinary shares stay constant in each case, so weighted-average and year-end shares match.
export const companyLearningAssumptions = [
  '兩家公司與全部數字均為虛構教學情境，非實際股票報價或買賣建議。',
  '財報金額以百萬元台幣表示，股數以百萬股表示，股價與 EPS 為元／股。',
  '假設無特別股、非控制權益或增減資；淨利歸屬普通股股東，全年加權平均與年底普通股股數相同。',
  'ROE 使用期初與期末權益的平均；P/B 使用期末每股淨值。調整股價只影響估值比率，不改寫已發生的營運成果。',
  '簡化自由現金流＝營業活動現金流−資本支出；未扣除全部還本與其他必要用途，不等於全可發股利的錢。',
  '本案例為簡化，將營業利益視為 EBIT；利息保障倍數＝營業利益÷利息費用。真實財報還須檢查業外項目。',
  '資料是精簡觀察表，未完整列示現金流調節、權益變動與財報附註，不能單憑這些比率判斷公司價值。',
];

export const companyLearningSources = [
  { label: 'CFA Institute｜財務比率定義（PDF）', url: 'https://www.cfainstitute.org/sites/default/files/-/media/documents/support/programs/cfa/cfa_program_level_ii_financial_ratio_list.pdf' },
  { label: 'SEC｜自由現金流定義與使用限制（Q102.07）', url: 'https://www.sec.gov/rules-regulations/staff-guidance/corporation-finance-interpretations/non-gaap-financial-measures' },
];

export const companyCases = [
  {
    id: 'qingchuan', name: '晴川日用品（虛構）', industry: '居家日用品製造',
    description: '營收與獲利持續成長，同時增加設備投入；應收帳款、庫存與借款也快速增加。練習分開閱讀成長、收款與資金壓力。',
    years: [
      { year: 2023, revenue: 1000, grossProfit: 300, operatingProfit: 120, netIncome: 72, operatingCashFlow: 90, capitalExpenditure: 80, assets: 1000, liabilities: 550, equity: 450, priorEquity: 400, interestBearingDebt: 350, currentAssets: 500, currentLiabilities: 300, interestExpense: 30, receivables: 180, inventory: 160, shares: 60, price: 30 },
      { year: 2024, revenue: 1200, grossProfit: 372, operatingProfit: 144, netIncome: 86, operatingCashFlow: 35, capitalExpenditure: 120, assets: 1250, liabilities: 750, equity: 500, priorEquity: 450, interestBearingDebt: 500, currentAssets: 670, currentLiabilities: 430, interestExpense: 36, receivables: 280, inventory: 230, shares: 60, price: 36 },
      { year: 2025, revenue: 1500, grossProfit: 480, operatingProfit: 180, netIncome: 108, operatingCashFlow: -20, capitalExpenditure: 160, assets: 1600, liabilities: 1040, equity: 560, priorEquity: 500, interestBearingDebt: 750, currentAssets: 850, currentLiabilities: 600, interestExpense: 45, receivables: 400, inventory: 330, shares: 60, price: 45 },
    ],
    questions: [
      {
        id: 'qingchuan-profit-cash', lessonId: 'financial-statements',
        question: '晴川 2025 年淨利 108 百萬元，營業活動現金流卻為 −20 百萬元。哪個判讀最有依據？',
        options: ['公司有獲利，所以不可能有現金壓力', '獲利與收付現金可能不同，應追查應收帳款、庫存及其他調整', '營業現金流為負就已證明財報造假'], answer: 1,
        explanation: '獲利不是現金流。案例的應收帳款與庫存增加，提供值得追查的方向，但精簡表未列完整調節，不能直接斷言全部原因或認定舞弊。',
      },
      {
        id: 'qingchuan-liabilities', lessonId: 'financial-debt',
        question: '晴川 2025 年總負債 1,040 百萬元，其中有息負債 750 百萬元。可以確認什麼？',
        options: ['全部 1,040 百萬元都一定收取利息', '兩個欄位互相矛盾，必有一個錯誤', '有息負債是總負債的一部分，剩下 290 百萬元不是此欄所列有息借款'], answer: 2,
        explanation: '1,040 − 750 = 290 百萬元。總負債可能還有應付款等項目；有息負債與全部負債不能當成同一個數字，完整內容仍要查附註。',
      },
      {
        id: 'qingchuan-price-only', lessonId: 'eps-pe',
        question: '保留晴川 2025 年淨利與股數，只把假設股價從 45 元調到 36 元，哪一組結果正確？',
        options: ['EPS 仍為 1.8 元，本益比從 25 倍降到 20 倍', 'EPS 從 1.8 元降到 1.44 元，本益比不變', '股價下降讓公司當年淨利也下降 20%'], answer: 0,
        explanation: 'EPS＝108 ÷ 60＝1.8 元；本益比由 45 ÷ 1.8＝25 倍變成 36 ÷ 1.8＝20 倍。改假設股價不會改變這份已給定的年度獲利。',
      },
    ],
  },
  {
    id: 'pingye', name: '平野日用品（虛構）', industry: '居家日用品製造',
    description: '營收成長較緩，營業活動持續帶來正現金流，設備投入與有息借款較平穩。練習觀察現金品質，同時保留對未來的疑問。',
    years: [
      { year: 2023, revenue: 800, grossProfit: 240, operatingProfit: 88, netIncome: 60, operatingCashFlow: 95, capitalExpenditure: 45, assets: 800, liabilities: 300, equity: 500, priorEquity: 450, interestBearingDebt: 120, currentAssets: 400, currentLiabilities: 180, interestExpense: 10, receivables: 120, inventory: 100, shares: 50, price: 24 },
      { year: 2024, revenue: 850, grossProfit: 263.5, operatingProfit: 102, netIncome: 70, operatingCashFlow: 105, capitalExpenditure: 50, assets: 850, liabilities: 300, equity: 550, priorEquity: 500, interestBearingDebt: 110, currentAssets: 420, currentLiabilities: 170, interestExpense: 9, receivables: 125, inventory: 105, shares: 50, price: 27 },
      { year: 2025, revenue: 900, grossProfit: 288, operatingProfit: 117, netIncome: 82, operatingCashFlow: 125, capitalExpenditure: 55, assets: 920, liabilities: 310, equity: 610, priorEquity: 550, interestBearingDebt: 100, currentAssets: 450, currentLiabilities: 170, interestExpense: 8, receivables: 130, inventory: 110, shares: 50, price: 30 },
    ],
    questions: [
      {
        id: 'pingye-cash-after-capex', lessonId: 'free-cash-flow',
        question: '依本案例的簡化自由現金流定義，平野 2025 年營業活動現金流 125 百萬元、資本支出 55 百萬元，計算結果是多少？',
        options: ['180 百萬元', '55 百萬元', '70 百萬元'], answer: 2,
        explanation: '125 − 55 = 70 百萬元。此數字未扣除所有還本及其他必要用途，不能直接解讀為全部都能配給股東。',
      },
      {
        id: 'pingye-average-equity', lessonId: 'roe-leverage',
        question: '依案例定義，平野 2025 年淨利 82 百萬元、期初權益 550 百萬元、期末權益 610 百萬元，ROE 最接近多少？',
        options: ['14.14%', '13.44%', '14.91%'], answer: 0,
        explanation: '平均權益＝（550 + 610）÷ 2＝580 百萬元；82 ÷ 580 × 100% ≈ 14.14%。只用期末或期初權益，會變成不同計算口徑。',
      },
      {
        id: 'pingye-future-limit', lessonId: 'information-check',
        question: '平野三年營業活動現金流都是正值。完成這項觀察後，還應保留哪個判斷？',
        options: ['未來每一年必定維持正現金流', '這是歷史情境，仍需了解客戶、競爭、支出需求與財報附註', '不需再考慮任何負債或價格因素'], answer: 1,
        explanation: '歷史數據提供觀察線索，不是未來保證。這份精簡案例沒有涵蓋所有營運與估值條件，也不能用來決定哪家公司一定值得買。',
      },
    ],
  },
];

const finite = value => typeof value === 'number' && Number.isFinite(value);
const positive = value => finite(value) && value > 0;
const nonnegative = value => finite(value) && value >= 0;
const finiteResult = value => Number.isFinite(value) ? value : null;
const divide = (numerator, denominator, scale = 1) => finite(numerator) && positive(denominator)
  ? finiteResult((numerator / denominator) * scale)
  : null;

/**
 * Percentages use 0–100 units; ratios use times; EPS uses NTD/share;
 * freeCashFlow uses the record's NTD-million unit. Invalid ratios are null.
 * Negative income keeps its signed EPS/margins/ROE when denominators are valid,
 * while P/E is unavailable for nonpositive EPS. P/B does not depend on income.
 */
export function calculateCompanyMetrics(record = {}, overridePrice) {
  const row = record || {};
  const price = overridePrice === undefined ? row.price : overridePrice;
  const eps = divide(row.netIncome, row.shares);
  const bookValuePerShare = positive(row.equity) ? divide(row.equity, row.shares) : null;
  const averageEquity = positive(row.priorEquity) && positive(row.equity)
    ? row.priorEquity / 2 + row.equity / 2
    : null;
  return {
    grossMargin: divide(row.grossProfit, row.revenue, 100),
    operatingMargin: divide(row.operatingProfit, row.revenue, 100),
    netMargin: divide(row.netIncome, row.revenue, 100),
    eps,
    roe: divide(row.netIncome, averageEquity, 100),
    pe: nonnegative(price) && positive(eps) ? divide(price, eps) : null,
    pb: nonnegative(price) && positive(bookValuePerShare) ? divide(price, bookValuePerShare) : null,
    freeCashFlow: finite(row.operatingCashFlow) && nonnegative(row.capitalExpenditure)
      ? finiteResult(row.operatingCashFlow - row.capitalExpenditure)
      : null,
    currentRatio: nonnegative(row.currentAssets) ? divide(row.currentAssets, row.currentLiabilities) : null,
    interestCoverage: divide(row.operatingProfit, row.interestExpense),
    liabilityRatio: nonnegative(row.liabilities) ? divide(row.liabilities, row.assets, 100) : null,
  };
}
